import type { ShipmentEmbryo } from '@/services/shipping/types';

import type { LiveEmbryoForPrint } from '../hooks/useFreezePdfPrint/types';
import { EmbryoFormValues } from '../recane-embryos-form/schema';
import type { RecaneCaneGroup } from './types';

export function groupEmbryosByNewCane(
  values: EmbryoFormValues | undefined,
): RecaneCaneGroup[] {
  if (!values?.embryos?.length) return [];

  const groups = new Map<string, RecaneCaneGroup>();

  for (const embryo of values.embryos) {
    const cane = embryo.newCane?.trim();
    if (!cane) continue;

    const existing = groups.get(cane);
    if (existing) {
      existing.embryoIds.push(embryo.embryoId);
      existing.embryoNames.push(embryo.name);
    } else {
      groups.set(cane, {
        cane,
        embryoIds: [embryo.embryoId],
        embryoNames: [embryo.name],
      });
    }
  }

  return Array.from(groups.values()).sort((a, b) =>
    a.cane.localeCompare(b.cane, undefined, {
      numeric: true,
      sensitivity: 'base',
    }),
  );
}

export function buildPrintEmbryos(
  groups: RecaneCaneGroup[],
  selectedCanes: string[],
  embryos: ShipmentEmbryo[],
): LiveEmbryoForPrint[] {
  const selected = new Set(selectedCanes);
  const embryoById = new Map(embryos.map((embryo) => [embryo.id, embryo]));

  return groups
    .filter((group) => selected.has(group.cane))
    .flatMap((group) =>
      group.embryoIds.reduce<LiveEmbryoForPrint[]>((acc, embryoId) => {
        const embryo = embryoById.get(embryoId);
        if (!embryo) return acc;

        acc.push({
          id: embryo.id,
          fertilizationId: embryo.fertilizationId ?? null,
          cane: group.cane,
          strawNumber: embryo.strawNumber ?? null,
          donorName: embryo.donor?.name ?? null,
          freezeMethod: embryo.freezeMethod
            ? { name: embryo.freezeMethod.name ?? null }
            : null,
        });

        return acc;
      }, []),
    );
}
